import { useState, useEffect, useRef } from 'react';
import { X } from '@phosphor-icons/react';

interface KeyboardShortcutsTrayProps {
  isOpen: boolean;
  onRequestClose: () => void;
  onCloseComplete: () => void;
}

export const KeyboardShortcutsTray = ({
  isOpen,
  onRequestClose,
  onCloseComplete,
}: KeyboardShortcutsTrayProps) => {
  const [visible, setVisible] = useState(false);
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    } else {
      setVisible(false);
      closeTimeout.current = setTimeout(() => {
        onCloseComplete();
      }, 300);
    }
    return () => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    };
  }, [isOpen, onCloseComplete]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onRequestClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onRequestClose]);

  return (
    <div
      className={
        isOpen
          ? 'fixed inset-0 z-50 flex items-end justify-center pointer-events-none'
          : 'hidden pointer-events-none'
      }
      aria-modal="false"
      role="dialog"
      aria-label="Keyboard Shortcuts"
    >
      <div
        className={`pointer-events-auto relative w-full md:w-4/6 lg:w-3/6 xl:w-2/5 max-w-lg mx-auto rounded-t-2xl shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-transform duration-300 ${
          visible ? 'translate-y-0' : 'translate-y-full'
        } z-10`}
        style={{ minHeight: '40vh', maxHeight: '80vh' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-12 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        <div className="relative flex items-center justify-center px-4 pt-4 pb-2 rounded-t-2xl bg-white dark:bg-gray-800">
          <button
            type="button"
            className="absolute right-4 top-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition cursor-pointer"
            onClick={onRequestClose}
            aria-label="Close"
            style={{ boxShadow: '0 1px 4px 0 rgba(0,0,0,0.04)' }}
          >
            <X className="text-gray-700 dark:text-gray-300" size={22} weight="bold" aria-hidden />
          </button>
          <div className="w-full flex justify-center">
            <span className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight" style={{ fontWeight: 500 }}>
              Keyboard Shortcuts
            </span>
          </div>
        </div>
        <div className="border-b border-gray-200 dark:border-gray-700" />
        <div className="p-6 overflow-y-auto max-h-[60vh] pb-10">
          <ul className="space-y-4">
            <li className="flex items-center gap-4">
              <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 font-mono border border-gray-200 shadow-sm">/</span>
              <span className="text-gray-800 dark:text-gray-200">Focus the search bar</span>
            </li>
            <li className="flex items-center gap-4">
              <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 font-mono border border-gray-200 shadow-sm">j</span>
              <span className="text-gray-800 dark:text-gray-200">Previous page (pagination)</span>
            </li>
            <li className="flex items-center gap-4">
              <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 font-mono border border-gray-200 shadow-sm">k</span>
              <span className="text-gray-800 dark:text-gray-200">Next page (pagination)</span>
            </li>
            <li className="flex items-center gap-4">
              <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 font-mono border border-gray-200 shadow-sm">?</span>
              <span className="text-gray-800 dark:text-gray-200">Open this help tray</span>
            </li>
            <li className="flex items-center gap-4">
              <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 font-mono border border-gray-200 shadow-sm">Esc</span>
              <span className="text-gray-800 dark:text-gray-200">Close tray or modal</span>
            </li>
          </ul>
          <div className="mt-8 text-xs text-gray-500 dark:text-gray-400">
            All shortcuts work on desktop (md and up). Some may not work if you are focused in a text field.
          </div>
        </div>
      </div>
    </div>
  );
};
